// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineAxisDensity } from '@/components/timeline-axis';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { Interval } from 'luxon';
import { useCallback, useRef } from 'react';

interface TimelineContainerOptions {
  density: TimelineAxisDensity;
  unitWidth: number;
}

/**
 * A React hook that calculates the width and height of an element containing
 * a timeline based on the interval and options provided.
 *
 * @param interval - The interval of time to display in the timeline.
 * @param options.unitWidth - The width in pixels of each unit in the timeline.
 * @param options.density - The density of the timeline axis.
 */
export function useTimelineContainer(
  interval: Interval,
  options: TimelineContainerOptions
) {
  const ref = useRef<HTMLDivElement>(null);
  const resizeObserver = useResizeObserver(ref, {
    // Use shorter debounce for timeline charts to be more responsive
    debounceMs: 30,
    // Allow more time for initial layout in complex timeline charts
    initialDelayMs: 150,
  });

  const unitCount = interval.length(options.density);
  const minimumWidth = unitCount * options.unitWidth;

  // Defensive width calculation:
  // 1. Use the larger of calculated minimum width or observed width
  // 2. Ensure we never have zero width (fallback to minimum)
  // 3. Only use observed width if it's reasonable (> 100px)
  const observedWidth = resizeObserver.width;
  const width =
    observedWidth > 100
      ? Math.max(minimumWidth, observedWidth)
      : Math.max(minimumWidth, 400); // Fallback to reasonable default

  // Trigger remeasurement - useful for parent components after animations
  const triggerRemeasure = useCallback(() => {
    resizeObserver.remeasure();
  }, [resizeObserver]);

  return {
    ref,
    width,
    height: resizeObserver.height,
    triggerRemeasure,
  };
}
